<template>
	<div class="admin-form">
		<div class="admin-form__header">
			<button
				class="admin-form__back"
				@click="router.push({ name: 'BlogAdmin' })"
			>
				← Back
			</button>
			<h1>{{ isEdit ? "Edit Blog" : "New Blog" }}</h1>
		</div>

		<form @submit.prevent="submit">
			<div class="admin-form__fields">
				<div class="admin-form__field">
					<label>Title <span class="req">*</span></label>
					<input v-model="form.title" type="text" required />
				</div>

				<div class="admin-form__field">
					<label>Description <span class="req">*</span></label>
					<textarea v-model="form.description" rows="3" required />
				</div>

				<div class="admin-form__row">
					<div class="admin-form__field">
						<label>Author</label>
						<input v-model="form.author" type="text" />
					</div>
					<div class="admin-form__field">
						<label>Read time (min)</label>
						<input v-model.number="form.readtime" type="number" min="1" />
					</div>
				</div>

				<div class="admin-form__field">
					<label>Link (slug) <span class="req">*</span></label>
					<input
						v-model="form.link"
						@input="onSlugInput"
						type="text"
						maxlength="255"
						:disabled="isEdit"
						placeholder="e.g. my-blog-post"
						:required="!isEdit"
					/>
				</div>

				<div class="admin-form__row">
					<div class="admin-form__field">
						<label>Thumbnail <span v-if="!isEdit" class="req">*</span></label>
						<div
							v-if="existingThumb && !deleteThumb"
							class="admin-form__preview"
						>
							<img :src="existingThumb" alt="Current thumbnail" />
							<button
								type="button"
								class="admin-form__remove-btn"
								@click="
									deleteThumb = true;
									existingThumb = null;
								"
							>
								Remove
							</button>
						</div>
						<template v-else>
							<input type="file" accept="image/*" @change="onThumbnail" />
							<span v-if="thumbnailStatus" class="admin-form__hint">{{
								thumbnailStatus
							}}</span>
						</template>
					</div>
					<div class="admin-form__field">
						<label>Cover image <span v-if="!isEdit" class="req">*</span></label>
						<div
							v-if="existingCover && !deleteCover"
							class="admin-form__preview"
						>
							<img :src="existingCover" alt="Current cover" />
							<button
								type="button"
								class="admin-form__remove-btn"
								@click="
									deleteCover = true;
									existingCover = null;
								"
							>
								Remove
							</button>
						</div>
						<template v-else>
							<input type="file" accept="image/*" @change="onCoverImage" />
							<span v-if="coverStatus" class="admin-form__hint">{{
								coverStatus
							}}</span>
						</template>
					</div>
				</div>

				<div class="admin-form__field">
					<label>Content folder</label>
					<p class="admin-form__hint">
						Drop or select a flat folder containing one <code>.md</code> file
						and its images.
					</p>
					<div
						class="admin-form__dropzone"
						:class="{ 'admin-form__dropzone--active': isDragging }"
						@dragover.prevent="isDragging = true"
						@dragleave="isDragging = false"
						@drop.prevent="onFolderDrop"
					>
						<span v-if="folderMdFile">
							{{ folderMdFile.name }} + {{ folderImageFiles.length }} image(s)
						</span>
						<span v-else>Drag a folder here, or</span>
						<label class="admin-form__folder-btn">
							Browse folder
							<input
								type="file"
								webkitdirectory
								style="display: none"
								@change="onFolderInput"
							/>
						</label>
					</div>
					<ul v-if="folderWarnings.length" class="admin-form__warnings">
						<li v-for="w in folderWarnings" :key="w">⚠ {{ w }}</li>
					</ul>
				</div>
			</div>

			<div v-if="submitError" class="admin-form__error">{{ submitError }}</div>
			<div v-if="submitSuccess" class="admin-form__success">
				Blog saved successfully!
			</div>

			<div class="admin-form__actions">
				<button type="submit" :disabled="!!submitStatus">
					{{ submitStatus || (isEdit ? "Save changes" : "Publish blog") }}
				</button>
			</div>
		</form>
	</div>
</template>

<script setup>
import { useRouter, useRoute } from "vue-router";
import { marked } from "marked";
import api from "@/api/index.js";

const router = useRouter();
const route = useRoute();

let sessionInterval = null;

async function checkSession() {
	try {
		await api.checkAdminSession();
	} catch {
		clearInterval(sessionInterval);
		localStorage.removeItem("sid");
		sessionStorage.removeItem("sid");
		router.push({ name: "BlogAdminLogin" });
	}
}

const isEdit = computed(() => !!route.params.link);

const form = reactive({
	title: "",
	description: "",
	author: "",
	readtime: null,
	link: "",
});

// Existing images in edit mode
const existingThumb = ref(null);
const existingCover = ref(null);
const deleteThumb = ref(false);
const deleteCover = ref(false);

// Single-image files (already resized blobs or null)
const thumbnailBlob = ref(null);
const coverBlob = ref(null);
const thumbnailStatus = ref("");
const coverStatus = ref("");

// Folder state
const folderMdFile = ref(null); // the single .md File
const folderImageFiles = ref([]); // files from the images/ subfolder
const isDragging = ref(false);
const folderWarnings = ref([]);

// Submit state
const submitStatus = ref(""); // empty = idle, string = current step label
const submitError = ref("");
const submitSuccess = ref(false);

// ─── Image resize helper ──────────────────────────────────────────────────────

function loadImage(src) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = src;
	});
}

function canvasToBlob(canvas, type, quality) {
	return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function resizeImage(file, maxBytes) {
	const dataUrl = await new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve(e.target.result);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

	const img = await loadImage(dataUrl);
	const type = file.type || "image/jpeg";
	const canvas = document.createElement("canvas");

	let width = img.naturalWidth;
	let height = img.naturalHeight;
	let quality = 0.9;

	while (width >= 50 && height >= 50) {
		canvas.width = width;
		canvas.height = height;
		canvas.getContext("2d").drawImage(img, 0, 0, width, height);
		const blob = await canvasToBlob(canvas, type, quality);
		if (blob.size <= maxBytes) return blob;

		quality -= 0.1;
		if (quality < 0.1) {
			width = Math.floor(width * 0.5);
			height = Math.floor(height * 0.5);
			quality = 0.8;
		}
		if (width < 50 || height < 50)
			throw new Error(`Cannot compress ${file.name} under 500 KB`);
	}
}

async function processSingleImage(file, labelRef, statusRef) {
	statusRef.value = "Resizing…";
	try {
		if (file.size <= 500_000) {
			labelRef.value = file;
			statusRef.value = `${(file.size / 1024).toFixed(0)} KB — ready`;
		} else {
			const blob = await resizeImage(file, 500_000);
			labelRef.value = new File([blob], file.name, { type: blob.type });
			statusRef.value = `Resized to ${(blob.size / 1024).toFixed(0)} KB — ready`;
		}
	} catch {
		statusRef.value = `⚠ Could not resize ${file.name}`;
		labelRef.value = null;
	}
}

async function onThumbnail(e) {
	const file = e.target.files[0];
	if (!file) return;
	await processSingleImage(file, thumbnailBlob, thumbnailStatus);
}

async function onCoverImage(e) {
	const file = e.target.files[0];
	if (!file) return;
	await processSingleImage(file, coverBlob, coverStatus);
}

// ─── Folder handling ──────────────────────────────────────────────────────────

function applyFolderSelection(mdFile, imageFiles) {
	folderWarnings.value = [];
	folderMdFile.value = mdFile ?? null;
	folderImageFiles.value = imageFiles;
}

// webkitdirectory input: files have webkitRelativePath like "folder/file.md"
// and "folder/images/img.jpg"
function onFolderInput(e) {
	const all = Array.from(e.target.files);
	const md =
		all.find((f) => {
			const parts = f.webkitRelativePath.split("/");
			return parts.length === 2 && f.name.endsWith(".md");
		}) ?? null;
	const images = all.filter((f) => {
		const parts = f.webkitRelativePath.split("/");
		return parts.length === 3 && parts[1].toLowerCase() === "images";
	});
	applyFolderSelection(md, images);
}

// Drag-and-drop: traverse one level deep, read root .md and images/ subdir
async function readDirEntries(dirEntry) {
	return new Promise((resolve) => {
		const reader = dirEntry.createReader();
		const all = [];
		function read() {
			reader.readEntries((entries) => {
				if (!entries.length) return resolve(all);
				all.push(...entries);
				read();
			});
		}
		read();
	});
}

async function entryToFile(fileEntry) {
	return new Promise((resolve, reject) => fileEntry.file(resolve, reject));
}

async function onFolderDrop(e) {
	isDragging.value = false;
	const items = Array.from(e.dataTransfer.items);

	let mdFile = null;
	let imageFiles = [];

	for (const item of items) {
		const entry = item.webkitGetAsEntry();
		if (!entry) continue;

		if (entry.isDirectory) {
			// This is the dropped folder — read its direct children
			const children = await readDirEntries(entry);
			for (const child of children) {
				if (child.isFile && child.name.endsWith(".md")) {
					mdFile = await entryToFile(child);
				} else if (child.isDirectory && child.name.toLowerCase() === "images") {
					const imgEntries = await readDirEntries(child);
					imageFiles = await Promise.all(
						imgEntries.filter((e) => e.isFile).map(entryToFile),
					);
				}
			}
		}
	}

	applyFolderSelection(mdFile, imageFiles);
}

// ─── Prefill for edit mode ────────────────────────────────────────────────────

onMounted(async () => {
	const sid = localStorage.getItem("sid");
	if (sid) sessionStorage.sid = sid;

	sessionInterval = setInterval(checkSession, 30_000);

	if (isEdit.value) {
		try {
			const res = await api.getBlog(route.params.link);
			const d = res.data;
			form.title = d.title ?? "";
			form.description = d.description ?? "";
			form.author = d.author ?? "";
			form.readtime = d.readtime ?? null;
			existingThumb.value = d.thumbnail ?? null;
			existingCover.value = d.coverImage ?? null;
		} catch {
			submitError.value = "Failed to load blog data.";
		}
	}
});

onUnmounted(() => clearInterval(sessionInterval));

// ─── Submit ───────────────────────────────────────────────────────────────────

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|svg|avif|bmp)$/i;
const MD_IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;
const KEBAB_CASE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function normalizeSlugInput(value) {
	return value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9-]+/g, "-")
		.replace(/-+/g, "-");
}

function toKebabCase(value) {
	return normalizeSlugInput(value).replace(/^-+|-+$/g, "");
}

function onSlugInput(e) {
	form.link = normalizeSlugInput(e.target.value);
}

async function readFileAsText(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve(e.target.result);
		reader.onerror = reject;
		reader.readAsText(file);
	});
}

async function submit() {
	submitError.value = "";
	submitSuccess.value = false;
	folderWarnings.value = [];

	if (!form.title || !form.description) {
		submitError.value = "Title and description are required.";
		return;
	}

	if (!isEdit.value) {
		form.link = toKebabCase(form.link);
		if (!form.link || !KEBAB_CASE_RE.test(form.link)) {
			submitError.value = "Link must be in kebab-case (e.g. my-blog-post).";
			return;
		}
	}

	// ── 1. Process folder ──
	let mdText = null;
	const imageIds = [];

	if (folderMdFile.value) {
		submitStatus.value = "Reading folder…";
		mdText = await readFileAsText(folderMdFile.value);

		// Map basename → full path as written in the md (e.g. "images/photo.jpg")
		const referenced = new Map();
		for (const match of mdText.matchAll(MD_IMAGE_RE)) {
			const fullPath = match[2];
			const basename = fullPath.split("/").pop();
			if (!referenced.has(basename)) referenced.set(basename, fullPath);
		}

		const toUpload = folderImageFiles.value.filter((f) =>
			referenced.has(f.name),
		);

		if (toUpload.length > 10) {
			folderWarnings.value.push(
				`Only the first 10 referenced images are included (${toUpload.length - 10} skipped).`,
			);
			toUpload.splice(10);
		}

		// ── 2. Resize images ──
		const readyImages = [];
		for (const file of toUpload) {
			submitStatus.value = `Resizing ${file.name}…`;
			try {
				let blob;
				if (file.size <= 500_000) {
					blob = file;
				} else {
					blob = await resizeImage(file, 500_000);
				}
				readyImages.push({ name: file.name, blob });
			} catch (err) {
				submitStatus.value = "";
				submitError.value = `Could not resize ${file.name} under 500 KB. Upload aborted.`;
				folderWarnings.value.push(err.message);
				return;
			}
		}

		// ── 3. Upload images ──
		for (let i = 0; i < readyImages.length; i++) {
			const { name, blob } = readyImages[i];
			submitStatus.value = `Uploading image ${i + 1}/${readyImages.length}…`;
			try {
				const formData = new FormData();
				formData.append(
					"image",
					blob instanceof File
						? blob
						: new File([blob], name, { type: blob.type }),
				);
				const res = await api.uploadBlogImage(formData);
				const id = res.data.id;
				imageIds.push(id);
				// Replace the full md path with basename-{id}.ext
				const mdPath = referenced.get(name) ?? name;
				const dotIdx = name.lastIndexOf(".");
				const newName =
					dotIdx !== -1
						? `${name.slice(0, dotIdx)}-${id}${name.slice(dotIdx)}`
						: `${name}-${id}`;
				mdText = mdText.replace(
					new RegExp(
						`(!\\[[^\\]]*\\]\\()${mdPath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\))`,
						"g",
					),
					`$1${newName}$2`,
				);
			} catch {
				submitStatus.value = "";
				submitError.value = `Failed to upload image ${name}. Upload aborted.`;
				return;
			}
		}
	}

	// ── 4. Upload thumbnail & cover if provided ──
	let thumbnailId = null;
	let coverId = null;

	if (thumbnailBlob.value) {
		submitStatus.value = "Uploading thumbnail…";
		try {
			const fd = new FormData();
			fd.append("image", thumbnailBlob.value);
			const res = await api.uploadBlogImage(fd);
			thumbnailId = res.data.id;
		} catch {
			submitStatus.value = "";
			submitError.value = "Failed to upload thumbnail.";
			return;
		}
	}

	if (coverBlob.value) {
		submitStatus.value = "Uploading cover image…";
		try {
			const fd = new FormData();
			fd.append("image", coverBlob.value);
			const res = await api.uploadBlogImage(fd);
			coverId = res.data.id;
		} catch {
			submitStatus.value = "";
			submitError.value = "Failed to upload cover image.";
			return;
		}
	}

	// ── 5. Save blog ──
	submitStatus.value = "Saving blog…";
	const html =
		mdText !== null
			? marked(mdText).replace(
					/(<img\s[^>]*src=")(?!https?:\/\/|\/)/g,
					"$1/api/images/",
				)
			: null;
	const payload = {
		title: form.title,
		description: form.description,
		author: form.author || undefined,
		readtime: form.readtime ?? undefined,
		imageIds,
		...(!isEdit.value && form.link && { link: form.link }),
		...(mdText !== null && { markdown: mdText, html }),
		...(thumbnailId !== null && { thumbnailId }),
		...(coverId !== null && { coverId }),
		...(deleteThumb.value && { delete_thumb: true }),
		...(deleteCover.value && { delete_cover: true }),
	};

	try {
		if (isEdit.value) {
			await api.updateBlog(route.params.link, payload);
		} else {
			await api.createBlog(payload);
		}
		submitSuccess.value = true;
		setTimeout(() => router.push({ name: "BlogAdmin" }), 1200);
	} catch {
		submitError.value = "Failed to save the blog. Please try again.";
	} finally {
		submitStatus.value = "";
	}
}
</script>

<style scoped lang="scss">
.admin-form {
	max-width: 760px;
	margin: 0 auto;
	padding: 40px 20px 80px;
}

.admin-form__header {
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 28px;

	h1 {
		margin: 0;
		font-size: 22px;
	}
}

.admin-form__back {
	background: none;
	border: none;
	font-size: 14px;
	cursor: pointer;
	color: #142c3a;
	padding: 0;

	&:hover {
		text-decoration: underline;
	}
}

.admin-form__fields {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.admin-form__row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 18px;
}

.admin-form__field {
	display: flex;
	flex-direction: column;
	gap: 5px;

	label {
		font-size: 13px;
		font-weight: 600;
	}

	input[type="text"],
	input[type="file"],
	textarea {
		padding: 8px 10px;
		border: 1px solid #ccc;
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
	}

	textarea {
		resize: vertical;
	}
}

.req {
	color: #c0392b;
}

.admin-form__hint {
	font-size: 12px;
	color: #7a8a99;
}

.admin-form__dropzone {
	border: 2px dashed #ccc;
	border-radius: 6px;
	padding: 24px;
	text-align: center;
	font-size: 14px;
	color: #5a6a78;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	transition: border-color 0.15s;

	&--active {
		border-color: #53aa6c;
		background: #f0faf3;
	}
}

.admin-form__folder-btn {
	display: inline-block;
	padding: 5px 12px;
	background: #142c3a;
	color: #fff;
	border-radius: 4px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.admin-form__warnings {
	margin: 6px 0 0;
	padding-left: 18px;
	font-size: 13px;
	color: #b7600a;

	li {
		margin-bottom: 2px;
	}
}

.admin-form__error {
	margin-top: 16px;
	padding: 10px 14px;
	background: #fdf0ee;
	border: 1px solid #f5c6c0;
	border-radius: 4px;
	color: #c0392b;
	font-size: 14px;
}

.admin-form__success {
	margin-top: 16px;
	padding: 10px 14px;
	background: #f0faf3;
	border: 1px solid #a8dbb8;
	border-radius: 4px;
	color: #1a7a3c;
	font-size: 14px;
}

.admin-form__actions {
	margin-top: 28px;

	button {
		padding: 10px 28px;
		background: #142c3a;
		color: #fff;
		border: none;
		border-radius: 4px;
		font-size: 15px;
		font-weight: 600;
		cursor: pointer;

		&:disabled {
			opacity: 0.5;
			cursor: default;
		}
	}
}

.admin-form__preview {
	position: relative;
	display: inline-flex;
	flex-direction: column;
	gap: 6px;

	img {
		max-width: 100%;
		max-height: 140px;
		object-fit: cover;
		border-radius: 4px;
		border: 1px solid #ccc;
	}
}

.admin-form__remove-btn {
	align-self: flex-start;
	padding: 3px 10px;
	background: #fff;
	border: 1px solid #e0574a;
	color: #c0392b;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		background: #fdf0ee;
	}
}

@media (max-width: 600px) {
	.admin-form__row {
		grid-template-columns: 1fr;
	}
}
</style>
